import { useCallback, useMemo, useState } from "react";
import useMerchantPortalAxios, {
  useMerchantPortalFormData,
} from "../../utils/axios";
import { setResponseData } from "../../features/transactionDetailsSlice";
import { useDispatch } from "react-redux";

const ISO_DATE_PATTERN =
  /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;

function normalizeTransactionStatus(status) {
  if (status instanceof Date) {
    return Number.isNaN(status.getTime()) ? "" : "APPROVED";
  }

  const normalizedStatus = String(status ?? "").trim();
  if (!normalizedStatus) return "";

  const isDateStatus =
    ISO_DATE_PATTERN.test(normalizedStatus) &&
    !Number.isNaN(Date.parse(normalizedStatus));

  return isDateStatus ? "APPROVED" : normalizedStatus;
}

export default function useNewTransactionDetailsAPI() {
  const client = useMerchantPortalAxios();
  const formClient = useMerchantPortalFormData();
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
const dispatch = useDispatch();
  const GetTransactionDetails = useCallback(
    async (TransactionID, AdditionalInfo = "", Status = "") => {
          dispatch(setResponseData({ field: "loading", value: true }));
      setDetailsLoading(true);
      setDetailsError(null);

      try {
        const response = await client.post(
          "/v1/TransactionDetails/GetTransactionDetails",
          {
            SessionID: sessionStorage.getItem("SessionID"),
            TransactionID,
            AdditionalInfo,
            Status: normalizeTransactionStatus(Status),
          },
        );
        const nextDetails = response.data.Payload?.TransactionDetails || null;

        setTransactionDetails(nextDetails);
        return nextDetails;
      } catch (error) {
        setDetailsError(error);
        setTransactionDetails(null);
        throw error;
      } finally {
        setDetailsLoading(false);
            dispatch(setResponseData({ field: "loading", value: false }));
      }
    },
    [client],
  );

  const resetTransactionDetails = useCallback(() => {
    setTransactionDetails(null);
    setDetailsError(null);
    setDetailsLoading(false);
  }, []);

  const UploadTransactionDetailsFile = useCallback(
    async (payload) => {
      const requestPayload = {
        SessionID: sessionStorage.getItem("SessionID"),
        TransactionID: payload.TransactionID,
        ReasonFile: payload.ReasonFile,
        IsInvoice: Boolean(payload.IsInvoice),
      };

      if (payload.ReasonFile) {
        const [meta = "", fileData = ""] = String(payload.ReasonFile).split(",");
        const mimeType = meta.split(":")[1]?.split(";")[0] || "application/octet-stream";
        const byteCharacters = atob(fileData);
        const byteArray = new Uint8Array(byteCharacters.length);

        for (let index = 0; index < byteCharacters.length; index += 1) {
          byteArray[index] = byteCharacters.charCodeAt(index);
        }

        requestPayload.ReasonFile = new File(
          [byteArray],
          payload.ReasonFileName,
          { type: mimeType },
        );
      }

      await formClient.post(
        "/v1/TransactionDetails/UploadTransactionDetailsFile",
        requestPayload,
      );
    },
    [formClient],
  );

  return useMemo(
    () => ({
      GetTransactionDetails,
      UploadTransactionDetailsFile,
      resetTransactionDetails,
      transactionDetails,
      detailsLoading,
      detailsError,
    }),
    [
      GetTransactionDetails,
      UploadTransactionDetailsFile,
      resetTransactionDetails,
      transactionDetails,
      detailsLoading,
      detailsError,
    ],
  );
}
